import { t } from 'i18next';

// Sanitizacao de nome de arquivo para paridade com o app mobile:
// - remove acentos (NFD + strip diacriticos);
// - remove caracteres invalidos de nome de arquivo: < > : " / \ | ? *;
// - espacos viram "_";
// - colapsa "_" repetidos e remove das pontas;
// - corta em 50 caracteres;
// - se ficar vazio, usa um fallback localizavel ("Arquivo").
export function sanitizeFileName(raw: string): string {
  const sanitized = (raw ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // remove diacriticos
    .replace(/[<>:"/\\|?*]/g, '') // remove chars invalidos
    .replace(/\s+/g, '_') // espacos viram "_"
    .replace(/_+/g, '_') // colapsa "_" repetidos
    .replace(/^_+|_+$/g, '') // remove "_" das pontas
    .slice(0, 50);

  return sanitized || t('file_default_name');
}

const PDF_EXTENSION = '.pdf';

export function ensurePdfExtension(name: string): string {
  const base = (name ?? '').trim().replace(/\.pdf$/i, '');
  return `${base || t('file_default_name')}${PDF_EXTENSION}`;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

// Data no formato dd-MM-yyyy (sem "/" para nao virar caractere invalido de arquivo).
function formatDateForFile(timestamp: number): string {
  const date = new Date(timestamp);
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;
}

// Hora no formato HH-mm-ss.
function formatTimeForFile(timestamp: number): string {
  const date = new Date(timestamp);
  return `${pad(date.getHours())}-${pad(date.getMinutes())}-${pad(
    date.getSeconds(),
  )}`;
}

// O prefixo org_{id}_ so aparece se houver org selecionada (id valido).
function orgPrefix(orgId?: number): string {
  return orgId ? `org_${orgId}_` : '';
}

interface IndividualNameParams {
  orgId?: number;
  formName: string;
  formDate: number;
}

// Individual: org_{idDaOrg}_{nomeDoFormulario}_{dd-MM-yyyy}_{HH-mm-ss}
// (o nome do formulario e' sanitizado/cortado; data e hora ja sao seguros).
export function buildIndividualPdfName({
  orgId,
  formName,
  formDate,
}: IndividualNameParams): string {
  const base = sanitizeFileName(formName);
  const date = formatDateForFile(formDate);
  const time = formatTimeForFile(formDate);
  return `${orgPrefix(orgId)}${base}_${date}_${time}`;
}

interface CombinedNameParams {
  orgId?: number;
  orgName?: string;
}

// Combinado: org_<org_id>_<org_name>.
export function buildCombinedPdfName({
  orgId,
  orgName,
}: CombinedNameParams): string {
  const prefix = orgId ? `org_${orgId}` : '';
  const org = orgName ? sanitizeFileName(orgName) : '';
  return [prefix, org].filter(Boolean).join('_') || t('file_default_name');
}
